import {
  faFilePowerpoint,
  faFloppyDisk,
  faFloppyDisks,
  faImage,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Grid, Table, TableBody, Theme, createStyles, makeStyles } from '@material-ui/core';
import { isNaN, orderBy } from 'lodash';
import React, { Dispatch, SetStateAction, useState } from 'react';
import VoxelButton from '../../../components/voxel/Buttons/VoxelButton';
import VoxelButtonGrid from '../../../components/voxel/Buttons/VoxelButtonGrid';
import { VoxelPopover } from '../../../components/voxel/Popover/VoxelPopover';
import { VoxelPopoverContent } from '../../../components/voxel/Popover/VoxelPopoverContent';
import { VoxelPopoverTrigger } from '../../../components/voxel/Popover/VoxelPopoverTrigger';
import { AnalysisConfigurationGq } from '../../../graphql/projects/models/AnalysisConfigurationGq';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';
import { UpdateAnalysisConfigurationCommandGq } from '../../../graphql/projects/mutations/projects/useUpdateAnalysisConfigurationGq';
import { projectsApiService } from '../../../graphql/projects/projectsApiService';
import { AnalysisListItem } from '../AnalysisList';
import { AnalysisFilterState } from '../Filter/analysisFilterState';
import AnalysisConfigurationRow from './AnalysisConfigurationRow';
import { DownloadBag } from './useAnalysisDownload';

export interface AnalysisConfigurationButtonProps {
  project: ProjectGq;
  selectedChart?: AnalysisListItem;
  setAnalysisFilterState: Dispatch<SetStateAction<AnalysisFilterState>>;
  setSelectedAnalysisId: (analysisId: string) => void;
  charts: AnalysisListItem[];
  downloadBag: DownloadBag;
}

const useClasses = makeStyles((theme: Theme) =>
  createStyles({
    tableGridItem: {
      maxHeight: 430,
      overflowY: 'scroll',
      display: 'block',
    },
    tableRow: {
      cursor: 'pointer',
    },
    tableRowDisabled: {
      backgroundColor: theme.palette.grey[50],
    },
    tableCellDisabled: {
      color: theme.palette.text.disabled,
    },
    paper: {
      width: 420,
      marginTop: theme.spacing(1),
      userSelect: 'none',
    },
  })
);

export const AnalysisConfigurationButton: React.FC<AnalysisConfigurationButtonProps> = ({
  project,
  selectedChart,
  setAnalysisFilterState,
  setSelectedAnalysisId,
  charts,
  downloadBag,
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);

  // Data
  const analysisConfigurationsQuery =
    projectsApiService.queries.analysis.useGetAnalysisConfigurations({ projectId: project.id });

  const [loading, setLoading] = useState(false);
  const [deletedIds, setDeletedIds] = useState<number[]>([]);

  // Create
  const createMutation = projectsApiService.mutations.projects.useCreateAnalysisConfiguration();
  const handleCreate = () => {
    setLoading(true);

    // Figure out number at end
    const matchingOldCharts = analysisConfigurationsQuery.result?.filter((oldChart) =>
      oldChart.title.toLowerCase().includes(selectedChart.title?.toLowerCase())
    );

    const existingNumbers = matchingOldCharts
      .map((oldChart) => {
        let potentialNumber = oldChart.title.slice(selectedChart.title.length + 2);
        potentialNumber = potentialNumber.slice(0, potentialNumber.length - 1);
        return +potentialNumber;
      })
      .filter((num) => !isNaN(num));

    const finalNumber = Math.max(0, matchingOldCharts?.length, ...existingNumbers) + 1;

    createMutation({
      title: `${selectedChart.title}${finalNumber > 1 ? ` (${finalNumber})` : ''}`,
      projectId: project.id,
      chartId: selectedChart.downloadId,
      analysisFilter: sessionStorage.getItem(`analysis_filter_${project.id}`),
    })
      .then(() => analysisConfigurationsQuery.query.refetch())
      .finally(() => setLoading(false));
  };

  // Create all
  const handleCreateAll = () => {
    setLoading(true);
    const promises = charts
      .filter((al) => !al.disabled)
      .map((analysis, index) =>
        createMutation({
          title: analysis.title,
          projectId: project.id,
          chartId: analysis.downloadId,
          analysisFilter: sessionStorage.getItem(`analysis_filter_${project.id}`),
          order: analysisConfigurationsQuery.result.length + index,
        })
      );

    Promise.all(promises)
      .then(() => analysisConfigurationsQuery.query.refetch())
      .finally(() => setLoading(false));
  };

  // Delete
  const deleteMutation = projectsApiService.mutations.projects.useDeleteAnalysisConfiguration();
  const handleDelete = (analysisConfiguration: AnalysisConfigurationGq) => {
    setDeletedIds((prev) => [...prev, analysisConfiguration.id]);
    deleteMutation({
      analysisConfigurationId: analysisConfiguration.id,
      projectId: analysisConfiguration.projectId,
    }).then(() => analysisConfigurationsQuery.query.refetch());
  };

  // Editing
  const [editId, setEditId] = useState<number>();
  const updateMutation = projectsApiService.mutations.projects.useUpdateAnalysisConfiguration();
  const handleUpdate = (command: UpdateAnalysisConfigurationCommandGq) => {
    updateMutation(command).then(() => analysisConfigurationsQuery.query.refetch());
  };

  // Select analysis
  const handleSelect = (analysisConfiguration: AnalysisConfigurationGq) => {
    const filter = JSON.parse(analysisConfiguration.analysisFilter) as AnalysisFilterState;
    const cleanedFilter = {
      ...filter,
      // Clean up filter - in case tag groups/domains don't longer exist
      tagGroupIds:
        filter.tagGroupIds?.filter((tagGroupId) =>
          project.tagGroups.some((tg) => tg.id === tagGroupId)
        ) ?? filter.tagGroupIds,
      tagGroupId: project.tagGroups.some((tg) => tg.id === filter?.tagGroupId)
        ? filter.tagGroupId
        : -1,
      domainIds:
        filter.domainIds?.filter((domainId) =>
          project.domains.some((domain) => domain.id === domainId)
        ) ?? filter.domainIds,
    };
    setAnalysisFilterState(cleanedFilter);
    setSelectedAnalysisId(analysisConfiguration.chartId);
  };

  // Download all
  const handleDownloadAllAsSlides = () => {
    setPopoverOpen(false);
    downloadBag.handleDownload(
      'slide',
      'All to slides',
      orderBy(analysisConfigurationsQuery.result, [(r) => r.order, (r) => r.id])
        .map((analysisConfiguration) => ({
          analysisConfiguration,
          analysisListChart: charts.find((al) => al.downloadId === analysisConfiguration.chartId),
        }))
        .filter((ac) => ac.analysisListChart?.disabled === false)
        .map((result) => ({
          chartId: result.analysisConfiguration.chartId,
          analysisFilter: result.analysisConfiguration.analysisFilter,
          title: result.analysisListChart.title,
        }))
    );
  };

  const handleDownloadAllAsImages = () => {
    setPopoverOpen(false);
    downloadBag.handleDownload(
      'screenshot',
      'All as PNG',
      orderBy(analysisConfigurationsQuery.result, [(r) => r.order, (r) => r.id])
        .map((analysisConfiguration) => ({
          analysisConfiguration,
          analysisListChart: charts.find((al) => al.downloadId === analysisConfiguration.chartId),
        }))
        .filter((ac) => ac.analysisListChart?.disabled === false)
        .map((result) => ({
          chartId: result.analysisConfiguration.chartId,
          analysisFilter: result.analysisConfiguration.analysisFilter,
          title: result.analysisListChart.title,
        }))
    );
  };

  // Styling
  const classes = useClasses();

  return (
    <VoxelPopover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <VoxelPopoverTrigger enableActive>
        <VoxelButton
          title="Saved charts"
          size="md"
          endIcon={<FontAwesomeIcon icon={faFloppyDisks} size="lg" />}
        />
      </VoxelPopoverTrigger>
      <VoxelPopoverContent paddingClass="p-4 w-[400px]">
        <div className="flex flex-col relative gap-2">
          <Grid item>
            <div className="pt-2">
              <div className="flex flex-row items-center">
                <VoxelButtonGrid>
                  <VoxelButton
                    title="Save current"
                    endIcon={<FontAwesomeIcon icon={faFloppyDisk} />}
                    tooltip="Save current analysis with filtering"
                    size="sm"
                    disabled={!selectedChart || loading}
                    onClick={handleCreate}
                  />
                  <VoxelButton
                    title="Save all"
                    endIcon={<FontAwesomeIcon icon={faFloppyDisks} />}
                    tooltip="Save all analyses with current filtering"
                    size="sm"
                    disabled={loading}
                    onClick={handleCreateAll}
                  />
                </VoxelButtonGrid>
              </div>
            </div>
          </Grid>
          <Grid item className={classes.tableGridItem}>
            <Table size="small">
              <TableBody>
                {orderBy(analysisConfigurationsQuery.result, [(r) => r.order, (r) => r.id]).map(
                  (analysisConfiguration) => (
                    <AnalysisConfigurationRow
                      key={analysisConfiguration.id}
                      editing={analysisConfiguration.id === editId}
                      setEditId={setEditId}
                      deletedIds={deletedIds}
                      handleDelete={handleDelete}
                      handleSelect={handleSelect}
                      handleUpdate={handleUpdate}
                      loading={loading}
                      charts={charts}
                      analysisConfiguration={analysisConfiguration}
                    />
                  )
                )}
              </TableBody>
            </Table>
          </Grid>
          <Grid item />
          <Grid item>
            <div className="p-2">
              <Button
                startIcon={<FontAwesomeIcon icon={faImage} />}
                fullWidth
                variant="outlined"
                disabled={!analysisConfigurationsQuery.result.length || loading}
                size="small"
                onClick={handleDownloadAllAsImages}
              >
                Export all as png
              </Button>
            </div>
            <div className="p-2 pb-4">
              <Button
                startIcon={<FontAwesomeIcon icon={faFilePowerpoint} />}
                fullWidth
                disabled={!analysisConfigurationsQuery.result.length || loading}
                variant="outlined"
                size="small"
                onClick={handleDownloadAllAsSlides}
              >
                Export all to Google slides
              </Button>
            </div>
          </Grid>
        </div>
      </VoxelPopoverContent>
    </VoxelPopover>
  );
};

export default AnalysisConfigurationButton;
